/**
 * Regroupement des mesures d'une section en phrases.
 *
 * Le sélecteur garde les numéros de mesure — ce sont les coordonnées du
 * morceau, et elles sont ce qui permet de se situer. Il gagne seulement des
 * respirations aux frontières de phrase, pour que l'élève voie *lesquelles
 * vont ensemble* et puisse boucler le groupe d'un geste.
 *
 * Deux sources, dans cet ordre :
 *
 * 1. Les phrases de l'analyseur, quand elles subdivisent réellement la
 *    section. Sur beaucoup de morceaux l'analyseur rend une phrase par
 *    section (`m1-13` pour une section de 13 mesures) : elle ne subdivise
 *    rien, et on passe à la suite.
 * 2. La coupe régulière toutes les 4 mesures, celle que
 *    `docs/spec/parcours-apprentissage-morceau.md` décrit déjà comme le
 *    découpage de repli.
 *
 * Volontairement pas les unités de travail de `work-units.ts` : elles sont
 * dosées pour la répétition (médiane 1 mesure), ce qui donnerait 7 groupes
 * sur une intro de 13 mesures — plus de séparateurs que de contenu.
 *
 * La sortie couvre toujours la section entièrement, sans trou ni
 * recouvrement : chaque mesure appartient à exactement un groupe.
 */

export interface PhraseGroup {
  readonly startMeasure: number;
  readonly endMeasure: number;
}

interface MeasureSpan {
  readonly startMeasure: number;
  readonly endMeasure: number;
}

/** Taille de la coupe de repli, en mesures. */
const FALLBACK_MEASURES_PER_GROUP = 4;

/**
 * En dessous de deux groupes, il n'y a rien à montrer : un groupe unique
 * couvrant toute la section est exactement la grille d'avant, avec un
 * séparateur en plus qui ne sépare rien.
 */
const MIN_USEFUL_GROUPS = 2;

export function buildPhraseGroups(
  section: MeasureSpan,
  phrases: readonly MeasureSpan[],
): PhraseGroup[] {
  if (
    !Number.isFinite(section.startMeasure) ||
    !Number.isFinite(section.endMeasure) ||
    section.endMeasure < section.startMeasure
  ) {
    return [];
  }

  const fromAnalysis = coverSection(section, phrases);

  if (fromAnalysis.length >= MIN_USEFUL_GROUPS) {
    return fromAnalysis;
  }

  return chunkSection(section);
}

/**
 * Projette les phrases qui touchent la section sur son intervalle, en
 * bouchant les trous et en tranchant les recouvrements.
 *
 * Une phrase de l'analyseur peut déborder d'une section, en couvrir
 * plusieurs, ou en laisser des mesures orphelines. On ne cherche pas à
 * corriger l'analyse : on garantit seulement que la grille reste une
 * partition de la section, parce qu'une mesure absente de l'écran est une
 * mesure sur laquelle on ne peut plus cliquer.
 */
function coverSection(
  section: MeasureSpan,
  phrases: readonly MeasureSpan[],
): PhraseGroup[] {
  const sorted = phrases
    .filter(
      (p) =>
        Number.isFinite(p.startMeasure) &&
        Number.isFinite(p.endMeasure) &&
        p.endMeasure >= p.startMeasure &&
        p.startMeasure <= section.endMeasure &&
        p.endMeasure >= section.startMeasure,
    )
    .slice()
    .sort((a, b) => a.startMeasure - b.startMeasure);

  const groups: PhraseGroup[] = [];
  let cursor = section.startMeasure;

  for (const phrase of sorted) {
    const start = Math.max(phrase.startMeasure, cursor);
    const end = Math.min(phrase.endMeasure, section.endMeasure);

    if (end < start) {
      continue;
    }

    if (start > cursor) {
      groups.push({ startMeasure: cursor, endMeasure: start - 1 });
    }

    groups.push({ startMeasure: start, endMeasure: end });
    cursor = end + 1;
  }

  if (cursor <= section.endMeasure) {
    groups.push({ startMeasure: cursor, endMeasure: section.endMeasure });
  }

  return groups;
}

function chunkSection(section: MeasureSpan): PhraseGroup[] {
  const groups: PhraseGroup[] = [];

  for (
    let start = section.startMeasure;
    start <= section.endMeasure;
    start += FALLBACK_MEASURES_PER_GROUP
  ) {
    groups.push({
      startMeasure: start,
      endMeasure: Math.min(
        start + FALLBACK_MEASURES_PER_GROUP - 1,
        section.endMeasure,
      ),
    });
  }

  return groups;
}

/** Le groupe est-il exactement la boucle en cours ? */
export function isGroupLooped(
  group: PhraseGroup,
  loop: MeasureSpan | null,
): boolean {
  if (loop === null) {
    return false;
  }

  return (
    loop.startMeasure === group.startMeasure &&
    loop.endMeasure === group.endMeasure
  );
}
